import React, { useState, useEffect } from 'react';
import { 
  Smartphone, Tablet, Download, QrCode, ShieldCheck, 
  ExternalLink, MessageCircle, FileText, WifiOff, 
  Layers, CheckCircle2, ChevronRight, X, Sparkles, Copy, Check
} from 'lucide-react';
import QRCode from 'qrcode';
import { useSiteSettings } from '../../contexts/SiteContexts';
import { toast } from 'react-hot-toast';
import { isMobileAppEnvironment } from '../../utils/appDetection';

interface AppInstallBannerProps {
  className?: string;
  variant?: 'featured' | 'compact' | 'drawer';
}

export const AppInstallBanner: React.FC<AppInstallBannerProps> = ({ 
  className = '',
  variant = 'featured'
}) => {
  const { settings } = useSiteSettings();
  const [showQrModal, setShowQrModal] = useState(false);
  const [showSpecsModal, setShowSpecsModal] = useState(false);
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState<string>('');
  const [copiedLink, setCopiedLink] = useState(false);
  const [isInsideMobileApp, setIsInsideMobileApp] = useState(false);

  useEffect(() => {
    setIsInsideMobileApp(isMobileAppEnvironment());
  }, []);

  const title = settings.appBannerTitle || 'Hamim Enterprises Official Mobile App';
  const version = settings.appVersionLabel || 'v1.0.0 (Official Edition)';
  const subtitle = settings.appBannerSubtitle || 'Fast 1-tap surgical catalog, product comparison, offline viewing & direct WhatsApp quotation';
  const rawReleaseNotes = settings.appReleaseNotes || 'Complete Surgical Instruments catalog, instant product inquiry, automated RFQ generator, offline browsing support';
  
  // Format release notes into bullet points if comma or newline separated
  const clinicalFeatures = rawReleaseNotes
    .split(/,|\n/)
    .map(s => s.trim())
    .filter(Boolean);

  // Determine APK download target: prefer actual server route or configured URL
  const hasApkUrl = settings.apkUrl && settings.apkUrl !== '#';
  const directApkUrl = hasApkUrl 
    ? (settings.apkUrl?.startsWith('http') || settings.apkUrl?.startsWith('/') ? settings.apkUrl : `/api/download-apk`)
    : '/api/download-apk';

  const playStoreUrl = settings.playStoreUrl && settings.playStoreUrl !== '#' ? settings.playStoreUrl : '#';
  const appStoreUrl = settings.appStoreUrl && settings.appStoreUrl !== '#' ? settings.appStoreUrl : '#';

  // Target URL for QR code scanning (mobile phone / tablet)
  const targetQrUrl = typeof window !== 'undefined' 
    ? (hasApkUrl ? (settings.apkUrl!.startsWith('http') ? settings.apkUrl! : `${window.location.origin}${settings.apkUrl}`) : `${window.location.origin}/download`)
    : '/download';

  useEffect(() => {
    if (settings.appBannerEnabled === false || isInsideMobileApp) return;

    QRCode.toDataURL(targetQrUrl, {
      width: 280,
      margin: 2,
      color: {
        dark: '#0f172a',
        light: '#ffffff'
      }
    })
      .then(url => setQrCodeDataUrl(url))
      .catch(err => console.error('QR generation error:', err));
  }, [targetQrUrl, settings.appBannerEnabled, isInsideMobileApp]);

  // If explicitly disabled in settings or if running inside installed mobile app (safely placed AFTER all hooks)
  if (settings.appBannerEnabled === false || isInsideMobileApp) {
    return null;
  }

  const handleCopyLink = () => {
    navigator.clipboard.writeText(targetQrUrl);
    setCopiedLink(true);
    toast.success('App installation link copied to clipboard!');
    setTimeout(() => setCopiedLink(false), 2000);
  };

  const handleWhatsAppQuotation = () => {
    const rawNumber = settings.whatsappNumber || '+923000000000';
    const cleanNumber = rawNumber.replace(/[^0-9]/g, '');
    const message = encodeURIComponent(
      `Hello Hamim Enterprises,\n\nI am contacting you regarding the ${title} (${version}).\nI would like to request an instant quotation and clinical catalog for surgical instruments.`
    );
    window.open(`https://wa.me/${cleanNumber}?text=${message}`, '_blank');
  };

  const handleApkDownloadClick = () => {
    toast.success('Downloading Hamim Enterprises Official Mobile APK...');
  };

  const handleStoreClick = (url: string, storeName: string, e: React.MouseEvent) => {
    if (url === '#') {
      e.preventDefault();
      toast(`${storeName} release link is being published. Use Direct Android APK download or WhatsApp for instant access!`, {
        icon: 'ℹ️'
      });
    }
  };

  return (
    <>
      <div className={`relative overflow-hidden rounded-[2.5rem] bg-gradient-to-br from-slate-950 via-slate-900 to-indigo-950 text-white shadow-2xl border border-indigo-500/20 ${className}`}>
        {/* Glow & Backdrop Ornaments */}
        <div className="absolute -top-32 -right-32 w-96 h-96 bg-indigo-500/15 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute -bottom-32 -left-32 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl pointer-events-none" />

        <div className="relative p-6 sm:p-8 md:p-12 z-10 flex flex-col xl:flex-row items-stretch xl:items-center justify-between gap-8 md:gap-12">
          {/* Left Column: Branding, Version, Value Prop, Features */}
          <div className="space-y-6 max-w-3xl">
            {/* Badges strip */}
            <div className="flex flex-wrap items-center gap-2.5">
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 bg-indigo-500/20 border border-indigo-400/30 rounded-full text-[11px] font-black uppercase tracking-wider text-indigo-300">
                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                <span>Hamim Enterprises Official Mobile App</span>
              </div>

              <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/10 rounded-full text-[11px] font-bold tracking-wide text-slate-200 border border-white/10">
                <Sparkles size={13} className="text-amber-400" />
                <span>{version}</span>
              </div>

              <div className="hidden sm:inline-flex items-center gap-1 px-3 py-1.5 bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 rounded-full text-[10px] font-bold uppercase tracking-wider">
                <ShieldCheck size={12} />
                <span>Verified Surgical Instruments</span>
              </div>
            </div>

            {/* Title */}
            <div>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-black uppercase tracking-tight text-white font-display leading-tight">
                {title}
              </h2>
              <p className="mt-3 text-sm sm:text-base text-slate-300 font-medium leading-relaxed">
                {subtitle}
              </p>
            </div>

            {/* Features Pills */}
            <div className="space-y-2">
              <div className="text-[10px] font-black uppercase tracking-widest text-indigo-300/80">
                Release Notes & App Highlights:
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                {clinicalFeatures.map((feature, idx) => (
                  <div 
                    key={idx} 
                    className="flex items-center gap-2.5 px-3.5 py-2 rounded-xl bg-white/5 border border-white/10 text-xs text-slate-200 font-medium"
                  >
                    <CheckCircle2 size={15} className="text-emerald-400 shrink-0" />
                    <span className="truncate">{feature}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Action Row */}
            <div className="flex flex-wrap items-center gap-3 pt-2">
              {/* Direct Android APK */}
              <a
                href={directApkUrl}
                download={settings.apkFileName || "hamim-enterprises-app.apk"}
                onClick={handleApkDownloadClick}
                className="group flex items-center gap-3 px-6 py-3.5 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-400 hover:to-teal-500 text-white font-black text-xs uppercase tracking-wider rounded-2xl transition-all shadow-lg shadow-emerald-500/25 active:scale-95"
              >
                <div className="p-1.5 bg-white/20 rounded-lg">
                  <Download size={16} className="group-hover:translate-y-0.5 transition-transform" />
                </div>
                <div className="text-left">
                  <div className="text-[9px] font-bold uppercase tracking-widest text-emerald-100 leading-none">Direct Download</div>
                  <div className="text-xs font-black tracking-tight leading-tight">Download Android APK</div>
                </div>
              </a>

              {/* Google Play Store */}
              <a
                href={playStoreUrl}
                target={playStoreUrl !== '#' ? '_blank' : undefined}
                rel="noreferrer"
                onClick={(e) => handleStoreClick(playStoreUrl, 'Google Play Store', e)}
                className="flex items-center gap-2.5 px-5 py-3.5 bg-white/10 hover:bg-white/15 text-white font-bold text-xs uppercase tracking-wider rounded-2xl transition-all border border-white/10 active:scale-95"
              >
                <svg className="w-4 h-4 fill-current text-white" viewBox="0 0 24 24">
                  <path d="M3.609 1.814L13.793 12 3.61 22.186a2.4 2.4 0 01-.61-1.636V3.45c0-.62.22-1.2.61-1.636zM15.207 13.414l2.457 2.457-12.78 7.378 10.323-9.835zm0-2.828L4.884.751l12.78 7.378-2.457 2.457zm1.414 1.414l3.774-2.18a1.5 1.5 0 010 2.6l-3.774 2.18z"/>
                </svg>
                <span>Google Play</span>
              </a>

              {/* Apple App Store */}
              <a
                href={appStoreUrl}
                target={appStoreUrl !== '#' ? '_blank' : undefined}
                rel="noreferrer"
                onClick={(e) => handleStoreClick(appStoreUrl, 'Apple App Store / TestFlight', e)}
                className="flex items-center gap-2.5 px-5 py-3.5 bg-white/10 hover:bg-white/15 text-white font-bold text-xs uppercase tracking-wider rounded-2xl transition-all border border-white/10 active:scale-95"
              >
                <svg className="w-4 h-4 fill-current text-white" viewBox="0 0 24 24">
                  <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M15.97 6.84c.63-.78 1.06-1.86.94-2.94-.92.04-2.02.61-2.67 1.39-.58.67-1.09 1.77-.95 2.83 1.03.08 2.05-.5 2.68-1.28z"/>
                </svg>
                <span>App Store</span>
              </a>

              {/* Instant WhatsApp Inquiry */}
              <button
                type="button"
                onClick={handleWhatsAppQuotation}
                className="flex items-center gap-2 px-5 py-3.5 bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs uppercase tracking-wider rounded-2xl transition-all shadow-md active:scale-95"
              >
                <MessageCircle size={16} />
                <span>WhatsApp Inquiry</span>
              </button>
            </div>
          </div>

          {/* Right Column: Interactive QR Code Card for Mobile Phones & Tablets */}
          <div className="xl:w-72 shrink-0 bg-white/10 backdrop-blur-xl border border-white/15 rounded-3xl p-6 flex flex-col items-center text-center shadow-xl">
            <div className="w-full flex items-center justify-between mb-3 text-slate-300">
              <span className="text-[10px] font-black uppercase tracking-wider text-indigo-300 flex items-center gap-1.5">
                <Tablet size={14} /> Android & Tablets
              </span>
              <span className="text-[10px] bg-indigo-500/30 text-indigo-200 px-2 py-0.5 rounded-full font-bold">
                Direct Scan
              </span>
            </div>

            {/* QR Code Container */}
            <div 
              onClick={() => setShowQrModal(true)}
              className="group/qr cursor-pointer relative bg-white p-3 rounded-2xl shadow-inner mb-4 transition-all hover:scale-105"
            >
              {qrCodeDataUrl ? (
                <img 
                  src={qrCodeDataUrl} 
                  alt="Scan to Install Hamim Enterprises App" 
                  className="w-40 h-40 object-contain mx-auto rounded-lg"
                />
              ) : (
                <div className="w-40 h-40 bg-slate-100 flex items-center justify-center rounded-lg">
                  <QrCode size={48} className="text-slate-400 animate-pulse" />
                </div>
              )}
              <div className="absolute inset-0 bg-indigo-900/80 rounded-2xl opacity-0 group-hover/qr:opacity-100 flex flex-col items-center justify-center text-white transition-opacity p-2">
                <QrCode size={28} className="mb-1" />
                <span className="text-[11px] font-black uppercase tracking-wider">Tap to Enlarge</span>
              </div>
            </div>

            <p className="text-[11px] text-slate-300 font-medium leading-tight mb-4">
              Point your Android phone, tablet, or camera at this QR code for instant direct APK installation.
            </p>

            <div className="w-full grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setShowQrModal(true)}
                className="w-full py-2.5 px-3 bg-white/10 hover:bg-white/20 rounded-xl text-[10px] font-black uppercase tracking-wider text-white transition-all flex items-center justify-center gap-1"
              >
                <QrCode size={13} />
                <span>QR View</span>
              </button>

              <button
                type="button"
                onClick={() => setShowSpecsModal(true)}
                className="w-full py-2.5 px-3 bg-indigo-600/50 hover:bg-indigo-600 rounded-xl text-[10px] font-black uppercase tracking-wider text-white transition-all flex items-center justify-center gap-1"
              >
                <FileText size={13} />
                <span>Details</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* QR Code Full Modal */}
      {showQrModal && (
        <div className="fixed inset-0 z-[200] bg-slate-950/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-[2.5rem] max-w-md w-full p-8 relative shadow-2xl border border-slate-100 animate-in fade-in zoom-in-95 duration-200">
            <button
              onClick={() => setShowQrModal(false)}
              className="absolute top-6 right-6 p-2 rounded-full bg-slate-100 text-slate-500 hover:bg-slate-200 transition-colors"
            >
              <X size={18} />
            </button>

            <div className="text-center space-y-4">
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-indigo-50 text-indigo-600 rounded-full text-[10px] font-black uppercase tracking-wider">
                <Tablet size={14} />
                <span>Direct Mobile Installation</span>
              </div>

              <h3 className="text-2xl font-black text-slate-900 uppercase tracking-tight font-display">
                Scan with Mobile Device
              </h3>

              <p className="text-xs text-slate-500 font-medium">
                Hold your device camera over the QR code to download the official Hamim Enterprises mobile APK and access the complete surgical instruments catalog.
              </p>

              {/* QR Image */}
              <div className="bg-slate-50 p-6 rounded-3xl border border-slate-100 inline-block shadow-sm">
                {qrCodeDataUrl ? (
                  <img src={qrCodeDataUrl} alt="Hamim Enterprises APK QR Code" className="w-56 h-56 mx-auto rounded-xl" />
                ) : (
                  <div className="w-56 h-56 flex items-center justify-center">
                    <QrCode size={64} className="text-slate-300 animate-pulse" />
                  </div>
                )}
              </div>

              {/* Device installation tips */}
              <div className="text-left bg-slate-50 p-4 rounded-2xl border border-slate-100 text-[11px] text-slate-600 space-y-2">
                <div className="font-black uppercase tracking-wider text-slate-800 text-[10px]">
                  Installation Protocol:
                </div>
                <div className="flex items-start gap-2">
                  <span className="font-bold text-indigo-600">1.</span>
                  <span>Scan QR code with your mobile camera or barcode scanner.</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="font-bold text-indigo-600">2.</span>
                  <span>Tap "Download APK" and allow installation if prompted by Android settings.</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="font-bold text-indigo-600">3.</span>
                  <span>Launch the Hamim Enterprises app for offline catalog browsing and instant orders.</span>
                </div>
              </div>

              {/* Copy URL */}
              <div className="flex items-center gap-2 pt-2">
                <input 
                  type="text" 
                  readOnly 
                  value={targetQrUrl} 
                  className="w-full px-4 py-3 bg-slate-100 rounded-xl text-xs font-mono text-slate-600 border border-slate-200 outline-none select-all"
                />
                <button
                  onClick={handleCopyLink}
                  className="px-4 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-black uppercase tracking-wider flex items-center gap-1.5 shrink-0 transition-all"
                >
                  {copiedLink ? <Check size={16} /> : <Copy size={16} />}
                  <span>{copiedLink ? 'Copied' : 'Copy'}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Release Notes & Specs Modal */}
      {showSpecsModal && (
        <div className="fixed inset-0 z-[200] bg-slate-950/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-[2.5rem] max-w-lg w-full p-8 relative shadow-2xl border border-slate-100 max-h-[90vh] overflow-y-auto">
            <button
              onClick={() => setShowSpecsModal(false)}
              className="absolute top-6 right-6 p-2 rounded-full bg-slate-100 text-slate-500 hover:bg-slate-200 transition-colors"
            >
              <X size={18} />
            </button>

            <div className="space-y-6">
              <div className="space-y-1">
                <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-indigo-50 text-indigo-600 rounded-full text-[10px] font-black uppercase tracking-wider">
                  <span>Official Release Information</span>
                </div>
                <h3 className="text-2xl font-black text-slate-900 uppercase tracking-tight font-display">
                  {title}
                </h3>
                <div className="text-xs font-bold text-slate-500">{version}</div>
              </div>

              <div className="p-4 bg-slate-50 rounded-2xl border border-slate-100 text-xs text-slate-700 leading-relaxed font-medium">
                {subtitle}
              </div>

              <div className="space-y-3">
                <h4 className="text-xs font-black uppercase tracking-widest text-slate-900">
                  Key Capabilities in this Release:
                </h4>
                <div className="space-y-2">
                  {clinicalFeatures.map((feat, i) => (
                    <div key={i} className="flex items-start gap-3 p-3 bg-slate-50 rounded-xl border border-slate-100">
                      <CheckCircle2 size={16} className="text-emerald-500 shrink-0 mt-0.5" />
                      <span className="text-xs font-medium text-slate-800">{feat}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="p-4 bg-indigo-50/70 border border-indigo-100 rounded-2xl space-y-2 text-xs text-indigo-900">
                <div className="font-black uppercase tracking-wider text-[11px] flex items-center gap-1.5">
                  <ShieldCheck size={14} className="text-indigo-600" />
                  Compatibility & Security
                </div>
                <p className="text-[11px] leading-relaxed text-indigo-800/90 font-medium">
                  Optimized for Android smartphones, tablets, and mobile devices. Includes offline database caching for operation theatres and clinics without Wi-Fi connectivity.
                </p>
              </div>

              <div className="pt-2 flex justify-end">
                <button
                  onClick={() => setShowSpecsModal(false)}
                  className="px-6 py-3 bg-slate-900 hover:bg-slate-800 text-white rounded-xl text-xs font-black uppercase tracking-wider"
                >
                  Close Details
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
